import {
  Button,
  CircularProgress,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Box,
  TextField,
  Grid,
  Modal,
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Settings,
  RestartAlt,
} from '@mui/icons-material';
import { useMemo, useState, useEffect, useCallback } from 'react';
import {
  ModalHeader,
  ModalTitleContainer,
  ModalTitle,
  ModalDescription,
  CloseButton,
  ModalContainer,
  ModalContent,
  ModalFooter,
} from './styles';
import { useExternalFont } from 'contexts/externalFontContext';
import type { IExternalFontExecutionViewModel } from 'viewModels';
import { IFontTestDossierDataInputModel } from 'inputModels';
import { X } from 'lucide-react';

interface FontTestModalProps {
  open: boolean;
  onClose: () => void;
  fontKey: string;
  fontName: string;
}

const DEFAULT_TIMEOUT = 5 * 60 * 1000; // 5 minutes default
const MAX_TIMEOUT = 5 * 60 * 1000; // 5 minutes max

// Default dossier data
const getDefaultDossierData = (
  fontKey: string
): IFontTestDossierDataInputModel => ({
  name: 'Nome',
  keywords: [],
  document: '70056979606',
  numberCda: '39357970010',
  federativeUnit: 'SP',
  expeditionOrgan: 'SSP',
  identityNumber: '39357970',
  birthDate: '2000-01-01T10:10:10.010Z',
  motherName: 'Nome da Mae',
  processNumber: '12122212222',
  fatherName: 'Nome do Pai',
  city: 'São Paulo',
  address: 'Endereço teste, 123',
  cep: '00000-000',
  neighborhood: 'Centro',
  font: fontKey,
});

export default function FontTestModal({
  open,
  onClose,
  fontKey,
  fontName,
}: FontTestModalProps) {
  const { executeFontTest, tests, clearTestsForFont } =
    useExternalFont();
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(-1);
  const [showDataForm, setShowDataForm] = useState(false);
  const [timeoutDuration, setTimeoutDuration] =
    useState(DEFAULT_TIMEOUT);
  const [timeRemaining, setTimeRemaining] = useState(timeoutDuration);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // New loading state
  const [dossierData, setDossierData] =
    useState<IFontTestDossierDataInputModel>(
      getDefaultDossierData(fontKey)
    );

  // Find the latest test for this fontKey
  const currentTest = useMemo<
    IExternalFontExecutionViewModel | undefined
  >(() => {
    const test = tests
      .filter((t) => t.fontKey === fontKey)
      .sort(
        (a, b) =>
          new Date(b.executionTime).getTime() -
          new Date(a.executionTime).getTime()
      )[0];
    return test;
  }, [tests, fontKey]);

  // Determine states
  const isRunning = currentTest?.running === true;
  const hasResult = currentTest && currentTest.running === false;

  // Timer effect for counting down during test
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (isRunning && !isTimedOut && currentTest?.executionTime) {
      const startTime = new Date(currentTest.executionTime).getTime();
      const updateTimer = () => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, timeoutDuration - elapsed);
        setTimeRemaining(remaining);
        if (remaining <= 0) {
          clearInterval(timer!);
          setIsTimedOut(true);
          setError(
            'A fonte não retornou dados dentro do tempo limite.'
          );
          setActiveStep(0);
        }
      };
      updateTimer();
      timer = setInterval(updateTimer, 1000);
    } else {
      setTimeRemaining(timeoutDuration);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isRunning, isTimedOut, currentTest, timeoutDuration]);

  // Reset timeout state when test completes or fails
  useEffect(() => {
    if (hasResult || !currentTest) {
      setIsTimedOut(false);
      setTimeRemaining(timeoutDuration);
      setError(null);
      setIsLoading(false); // Reset loading when test completes
    }
  }, [hasResult, currentTest, timeoutDuration]);

  // Handler for form input changes
  const handleInputChange = (
    field: keyof IFontTestDossierDataInputModel,
    value: any
  ) => {
    setDossierData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handler for timeout input change
  const handleTimeoutChange = (value: string) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue <= 0) {
      setTimeoutDuration(DEFAULT_TIMEOUT);
      setTimeRemaining(DEFAULT_TIMEOUT);
    } else {
      const newTimeout = Math.min(numValue * 1000, MAX_TIMEOUT);
      setTimeoutDuration(newTimeout);
      setTimeRemaining(newTimeout);
    }
  };

  const handleStart = useCallback(async () => {
    setError(null);
    setIsTimedOut(false);
    setActiveStep(0);
    setTimeRemaining(timeoutDuration);
    setIsLoading(true); // Set loading state
    try {
      // Clear previous tests for this fontKey before starting a new one
      clearTestsForFont(fontKey);
      await executeFontTest(dossierData);
      setActiveStep(0);
      setShowDataForm(false);
    } catch (err) {
      setError('Erro ao iniciar o teste');
      setActiveStep(-1);
      setIsLoading(false); // Reset loading on error
    }
  }, [
    executeFontTest,
    dossierData,
    fontKey,
    clearTestsForFont,
    timeoutDuration,
  ]);

  const resetData = () => {
    setDossierData(getDefaultDossierData(fontKey));
    setTimeoutDuration(DEFAULT_TIMEOUT);
    setTimeRemaining(DEFAULT_TIMEOUT);
    setError(null);
    setIsTimedOut(false);
    setIsLoading(false); // Reset loading on data reset
  };

  // Steps for the test process
  const steps = [
    {
      label: 'Requisição Enviada',
      description: 'Enviando solicitação ao servidor',
    },
    {
      label: 'Executando Robô',
      description: 'O robô está sendo executado',
    },
    { label: 'Resultados', description: 'Processamento concluído' },
  ];

  // Update active step based on test state
  useEffect(() => {
    if (!currentTest) {
      setActiveStep(-1);
      setIsLoading(false); // Reset loading if no test
    } else if (isTimedOut) {
      setActiveStep(0);
      setIsLoading(false); // Reset loading on timeout
    } else if (isRunning) {
      setActiveStep(1);
    } else if (hasResult) {
      setActiveStep(2);
      setIsLoading(false); // Reset loading when results are available
    }
  }, [currentTest, isRunning, hasResult, isTimedOut]);

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setError(null);
      setShowDataForm(false);
      if (!currentTest) {
        setActiveStep(-1);
      }
      setTimeRemaining(timeoutDuration);
      setIsTimedOut(false);
      setIsLoading(false); // Reset loading when modal closes
    }
  }, [open, currentTest, timeoutDuration]);

  // Reset dossier data and timeout when fontKey changes
  useEffect(() => {
    setDossierData(getDefaultDossierData(fontKey));
    setTimeoutDuration(DEFAULT_TIMEOUT);
    setTimeRemaining(DEFAULT_TIMEOUT);
    setError(null);
    setIsTimedOut(false);
    setIsLoading(false); // Reset loading when fontKey changes
  }, [fontKey]);

  // Format time remaining as MM:SS
  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds
      .toString()
      .padStart(2, '0')}`;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      sx={{ display: 'flex', flexDirection: 'column' }}
    >
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: 2,
          outline: 'none',
          width: { xs: '90vw', sm: '80vw', md: '60vw' },
          minHeight: { xs: '70vh', sm: '80vh' },
          maxWidth: '100%',
        }}
      >
        <ModalContainer>
          <ModalHeader>
            <ModalTitleContainer>
              <ModalTitle id="modal-title">
                Teste de Execução - {fontName}
              </ModalTitle>
              <ModalDescription id="modal-description">
                Teste o funcionamento da fonte e valide os resultados
                retornados por ela
              </ModalDescription>
            </ModalTitleContainer>
            <CloseButton onClick={onClose}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>
          <ModalContent>
            {!showDataForm && (
              <Box sx={{ width: '100%', mb: 4 }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map((step, index) => (
                    <Step key={step.label}>
                      <StepLabel
                        optional={
                          <Typography variant="caption">
                            {step.description}
                          </Typography>
                        }
                        error={isTimedOut && index === 0}
                      >
                        {step.label}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>
            )}

            {showDataForm && (
              <Paper sx={{ p: 2, mb: 3, borderRadius: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <Settings sx={{ mr: 1, fontSize: 20 }} />{' '}
                    Configuração dos Dados de Teste
                  </Typography>
                  <Box>
                    <Button
                      startIcon={<RestartAlt />}
                      variant="outlined"
                      color="secondary"
                      onClick={resetData}
                      size="small"
                    >
                      Resetar
                    </Button>
                  </Box>
                </Box>

                <Grid container spacing={1}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Nome"
                      fullWidth
                      value={dossierData.name}
                      onChange={(e) =>
                        handleInputChange('name', e.target.value)
                      }
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="CPF/Documento"
                      fullWidth
                      value={dossierData.document}
                      onChange={(e) =>
                        handleInputChange('document', e.target.value)
                      }
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Número CDA"
                      fullWidth
                      value={dossierData.numberCda}
                      onChange={(e) =>
                        handleInputChange('numberCda', e.target.value)
                      }
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="UF"
                      fullWidth
                      value={dossierData.federativeUnit}
                      onChange={(e) =>
                        handleInputChange(
                          'federativeUnit',
                          e.target.value
                        )
                      }
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Órgão Expedidor"
                      fullWidth
                      value={dossierData.expeditionOrgan}
                      onChange={(e) =>
                        handleInputChange(
                          'expeditionOrgan',
                          e.target.value
                        )
                      }
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Número de Identidade"
                      fullWidth
                      value={dossierData.identityNumber}
                      onChange={(e) =>
                        handleInputChange(
                          'identityNumber',
                          e.target.value
                        )
                      }
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Data de Nascimento"
                      type="date"
                      fullWidth
                      value={dossierData.birthDate.substring(0, 10)}
                      onChange={(e) =>
                        handleInputChange(
                          'birthDate',
                          `${e.target.value}T10:10:10.010Z`
                        )
                      }
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Nome da Mãe"
                      fullWidth
                      value={dossierData.motherName}
                      onChange={(e) =>
                        handleInputChange(
                          'motherName',
                          e.target.value
                        )
                      }
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Nome do Pai"
                      fullWidth
                      value={dossierData.fatherName}
                      onChange={(e) =>
                        handleInputChange(
                          'fatherName',
                          e.target.value
                        )
                      }
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Número do Processo"
                      fullWidth
                      value={dossierData.processNumber}
                      onChange={(e) =>
                        handleInputChange(
                          'processNumber',
                          e.target.value
                        )
                      }
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Cidade"
                      fullWidth
                      value={dossierData.city}
                      onChange={(e) =>
                        handleInputChange('city', e.target.value)
                      }
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Endereço"
                      fullWidth
                      value={dossierData.address}
                      onChange={(e) =>
                        handleInputChange('address', e.target.value)
                      }
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="CEP"
                      fullWidth
                      value={dossierData.cep}
                      onChange={(e) =>
                        handleInputChange('cep', e.target.value)
                      }
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Bairro"
                      fullWidth
                      value={dossierData.neighborhood}
                      onChange={(e) =>
                        handleInputChange(
                          'neighborhood',
                          e.target.value
                        )
                      }
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Timeout (segundos)"
                      type="number"
                      fullWidth
                      value={timeoutDuration / 1000}
                      onChange={(e) =>
                        handleTimeoutChange(e.target.value)
                      }
                      InputProps={{
                        inputProps: { min: 1, max: 300 },
                      }}
                      helperText="Máximo 300 segundos (5 minutos)"
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Palavras-chave (separadas por vírgula)"
                      fullWidth
                      value={dossierData.keywords.join(',')}
                      onChange={(e) =>
                        handleInputChange(
                          'keywords',
                          e.target.value.split(',')
                        )
                      }
                      helperText="Digite palavras-chave separadas por vírgula"
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                </Grid>
              </Paper>
            )}

            {activeStep === -1 &&
              !error &&
              !showDataForm &&
              !isLoading && (
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 3,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Clique no botão abaixo para iniciar o teste da
                    fonte.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleStart}
                    size="large"
                    sx={{ px: 4 }}
                  >
                    Iniciar Teste
                  </Button>
                </Box>
              )}

            {isLoading &&
              activeStep === 0 &&
              !isTimedOut &&
              !error &&
              !showDataForm && (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                  <CircularProgress
                    size={30}
                    color="primary"
                    sx={{ mr: 2 }}
                  />
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Iniciando teste...
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Enviando solicitação ao servidor. Aguarde.
                  </Typography>
                </Box>
              )}

            {error && !showDataForm && (
              <Paper
                elevation={0}
                sx={{
                  textAlign: 'center',
                  p: 3,
                  bgcolor: 'error.light',
                  color: 'error.contrastText',
                  borderRadius: 1,
                }}
              >
                <Error sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h6">{error}</Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {error.includes('tempo limite')
                    ? 'O teste demorou mais do que o esperado. Tente novamente.'
                    : 'Ocorreu um erro durante o teste. Tente novamente.'}
                </Typography>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleStart}
                  sx={{ mt: 2 }}
                >
                  Tentar Novamente
                </Button>
              </Paper>
            )}

            {isRunning && !isTimedOut && !error && !showDataForm && (
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <CircularProgress
                  size={30}
                  color="primary"
                  sx={{ mr: 2 }}
                />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Executando teste...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {`O robô está processando sua solicitação. Tempo máximo restante: ${formatTime(
                    timeRemaining
                  )}.`}
                </Typography>
              </Box>
            )}

            {hasResult && currentTest && !showDataForm && (
              <Box sx={{ width: '100%' }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 3,
                    bgcolor: currentTest.success
                      ? 'success.main'
                      : 'error.main',
                    borderRadius: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      mb: 2,
                    }}
                  >
                    {currentTest.success ? (
                      <CheckCircle
                        sx={{ mr: 1, color: 'white', mt: 0.5 }}
                      />
                    ) : (
                      <Error
                        sx={{ mr: 1, color: 'white', mt: 0.5 }}
                      />
                    )}
                    <Typography variant="h6" sx={{ color: 'white' }}>
                      {currentTest.success
                        ? 'Teste concluído com sucesso'
                        : 'Falha no teste'}
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                        >
                          Tempo de Execução
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ color: 'white' }}
                        >
                          {currentTest.executionTimeSeconds?.toFixed(
                            2
                          ) || 'N/A'}{' '}
                          segundos
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                        >
                          Id da execução
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'white',
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                          }}
                        >
                          {currentTest.id}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                        >
                          Fonte
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ color: 'white' }}
                        >
                          {fontKey}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                        >
                          Data
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ color: 'white' }}
                        >
                          {new Date(
                            currentTest.executionTime
                          ).toLocaleString()}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>

                <Typography variant="h6" gutterBottom>
                  Retorno da Fonte
                </Typography>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    maxHeight: '400px',
                    overflow: 'auto',
                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {JSON.stringify(currentTest.result, null, 2) ||
                    'Sem dados retornados'}
                </Paper>
              </Box>
            )}
          </ModalContent>
          <ModalFooter>
            {showDataForm ? (
              <>
                <Button
                  variant="outlined"
                  onClick={resetData}
                  sx={{ mr: 1 }}
                  size="small"
                  startIcon={<RestartAlt />}
                >
                  Resetar Dados
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  variant="outlined"
                  onClick={() => setShowDataForm(false)}
                  sx={{ mr: 1 }}
                  size="small"
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleStart}
                  size="small"
                  disabled={isLoading} // Disable button while loading
                >
                  Iniciar Teste
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => setShowDataForm(true)}
                  variant="outlined"
                  sx={{ minWidth: '150px', mr: 1 }}
                  startIcon={<Settings />}
                  disabled={(isRunning && !isTimedOut) || isLoading}
                  size="small"
                >
                  Configurar Dados
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  onClick={handleStart}
                  disabled={(isRunning && !isTimedOut) || isLoading}
                  variant={hasResult ? 'outlined' : 'contained'}
                  sx={{ minWidth: '120px', mr: 1 }}
                  size="small"
                >
                  {isRunning && !isTimedOut
                    ? 'Teste em Andamento'
                    : 'Testar Novamente'}
                </Button>
                <Button
                  onClick={onClose}
                  sx={{ minWidth: '100px' }}
                  size="small"
                >
                  Fechar
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContainer>
      </Paper>
    </Modal>
  );
}
